import React, { useState, useCallback } from "react";
import { Offcanvas } from "react-bootstrap";

export interface PaymentRow {
    id: number;
    name: string;
    payment_date: string;
    percentage: string;
    amount: string;
    lastEdited?: "percentage" | "amount" | "";
}

interface ValidationError {
    rowId?: number;
    message: string;
}

interface PaymentScheduleProps {
    totalAmount: number;
    onSubmit?: (rows: PaymentRow[], totalAmount: number) => void;
    isShow: boolean;
    isClose: (data: any) => void;
    initialRows?: PaymentRow[];
}

let _idCounter = 0;
function nextId(): number {
    return ++_idCounter;
}

function formatINR(value: number): string {
    return (
        "₹" +
        Math.round(value).toLocaleString("en-IN")
    );
}

const inputBase: React.CSSProperties = {
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 14,
    color: "#111827",
    background: "#fff",
    outline: "none",
    width: "100%",
    transition: "border-color 0.15s",
};

const PaymentSchedule: React.FC<PaymentScheduleProps> = ({ totalAmount, onSubmit, isShow, isClose, initialRows }) => {
    const total = totalAmount;
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [errors, setErrors] = useState<ValidationError[]>([]);
    const [submitted, setSubmitted] = useState(false);

    // Recalculate amounts when totalAmount prop changes
    React.useEffect(() => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.lastEdited === "percentage" && r.percentage !== "") {
                    const pct = parseFloat(r.percentage) || 0;
                    return { ...r, amount: totalAmount > 0 ? String(Math.round((pct / 100) * totalAmount)) : "" };
                } else if (r.lastEdited === "amount" && r.amount !== "" && totalAmount > 0) {
                    const amt = parseFloat(r.amount) || 0;
                    return { ...r, percentage: ((amt / totalAmount) * 100).toFixed(2) };
                }
                return r;
            })
        );
    }, [totalAmount]);

    React.useEffect(() => {
        if (initialRows && initialRows.length > 1 && isShow) {
            setRows(initialRows);
        }
    }, [initialRows, isShow]);

    // ── derived summary ──────────────────────────────────────────────
    const sumPct = rows.reduce((acc, r) => acc + (parseFloat(r.percentage) || 0), 0);
    const sumAmt = rows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
    const remaining = total - sumAmt;
    const pctOk = Math.abs(sumPct - 100) < 0.01;

    // ── handlers ────────────────────────────────────────────────────
    const handlePercentageChange = useCallback((id: number, value: string) => {
        setErrors([]);
        setSubmitted(false);
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const pct = parseFloat(value);
                const newAmt = !isNaN(pct) && total > 0 ? String((pct / 100) * total) : "";
                return { ...r, percentage: value, amount: Number(newAmt).toFixed(3), lastEdited: "percentage" };
            })
        );
    },
        [total]
    );

    const handleAmountChange = ((id: number, value: string) => {
        setErrors([]);
        setSubmitted(false);
        const newRows = rows || [];
        const updatedRows: PaymentRow[] = newRows.map((r) => {
            if (r.id !== id) return r;
            const amt = parseFloat(value);
            const newPct =
                !isNaN(amt) && total > 0
                    ? ((amt / total) * 100).toFixed(3)
                    : "";
            return { ...r, amount: value, percentage: newPct, lastEdited: "amount" };
        });
        setRows(updatedRows);
    });

    const handleNameChange = ((id: number, value: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, name: value } : r))
        );
    });

    const handleDateChange = useCallback((id: number, value: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, payment_date: value } : r))
        );
        setErrors([]);
        setSubmitted(false);
    }, []);

    const addRow = useCallback(() => {
        setRows((prev) => [
            ...prev,
            { id: nextId(), name: "", payment_date: "", percentage: "", amount: "", lastEdited: "" },
        ]);
        setErrors([]);
        setSubmitted(false);
    }, []);

    const removeRow = useCallback((id: number) => {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setErrors([]);
        setSubmitted(false);
    }, []);

    const handleSubmit = useCallback(() => {
        const errs: ValidationError[] = [];

        if (rows.length === 0) {
            errs.push({ message: "Please add at least one installment." });
        }

        rows.forEach((r, i) => {
            const pct = Number(parseFloat(r.percentage).toFixed(2));
            const amt = Number(parseFloat(r.amount).toFixed(2));

            if (!r.payment_date) {
                errs.push({ rowId: r.id, message: `Installment ${i + 1}: Date is required.` });
            }

            if (isNaN(pct) || pct <= 0) {
                errs.push({ rowId: r.id, message: `Installment ${i + 1}: Enter a valid percentage greater than 0.` });
            }

            if (isNaN(amt) || amt <= 0) {
                errs.push({ rowId: r.id, message: `Installment ${i + 1}: Enter a valid amount greater than 0.` });
            }
        });

        if (rows.length > 0 && Math.abs(sumPct - 100) > 0.05) {
            const diff = (100 - sumPct).toFixed(1);

            errs.push({
                message: `Total percentage is ${sumPct.toFixed(1)}%. It must be exactly 100%. ${parseFloat(diff) > 0
                    ? `${diff}% is remaining.`
                    : `${Math.abs(parseFloat(diff))}% is over-allocated.`
                    }`,
            });
        }

        if ((sumAmt - total) > 1) {
            errs.push({
                message: `Total amount exceeds the allowed limit by ${formatINR(sumAmt - total)}.`,
            });
        }

        if (errs.length > 0) {
            setErrors(errs);
            setSubmitted(false);
            return;
        }

        setErrors([]);
        setSubmitted(true);
        onSubmit?.(rows, total);
    }, [rows, sumPct, sumAmt, total, onSubmit]);

    const errorRowIds = new Set(errors.filter((e) => e.rowId).map((e) => e.rowId!));

    // ── styles ───────────────────────────────────────────────────────
    const styles = {
        container: {
            fontFamily: "'DM Sans', system-ui, sans-serif",
            maxWidth: 720,
            margin: "0 auto",
            padding: "24px 0",
        } as React.CSSProperties,

        card: (hasError: boolean): React.CSSProperties => ({
            background: "#fff",
            border: `1px solid ${hasError ? "#f87171" : "#e5e7eb"}`,
            borderRadius: 12,
            padding: "12px 14px",
        }),

        totalCard: {
            background: "#f9fafb",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        } as React.CSSProperties,

        totalInput: {
            fontSize: 20,
            fontWeight: 500,
            background: "transparent",
            border: "none",
            outline: "none",
            textAlign: "right",
            width: 160,
            color: "#111827",
        } as React.CSSProperties,

        grid3: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
        } as React.CSSProperties,

        summaryGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
            marginBottom: 16,
        } as React.CSSProperties,

        sumCard: (variant: "default" | "ok" | "warn"): React.CSSProperties => ({
            background: variant === "ok" ? "#f0fdf4" : variant === "warn" ? "#fef2f2" : "#f9fafb",
            borderRadius: 8,
            padding: "10px 12px",
            textAlign: "center",
        }),

        submitBtn: {
            width: "100%",
            padding: "11px",
            background: "#111827",
            border: "none",
            borderRadius: 10,
            color: "#fff",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
        } as React.CSSProperties,

        addBtn: {
            width: "100%",
            padding: "9px",
            border: "1px dashed #9ca3af",
            borderRadius: 10,
            background: "transparent",
            color: "#6b7280",
            fontSize: 13,
            cursor: "pointer",
            marginBottom: 16,
        } as React.CSSProperties,

        errorBanner: {
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 13,
            color: "#b91c1c",
            marginBottom: 12,
        } as React.CSSProperties,

        successBanner: {
            background: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 13,
            color: "#15803d",
            marginBottom: 12,
        } as React.CSSProperties,
    };

    return (
        <Offcanvas show={isShow} onHide={isClose} placement="end" className="offcanvas-large">
            <Offcanvas.Header closeButton><Offcanvas.Title>Payment Schedule</Offcanvas.Title></Offcanvas.Header>
            <Offcanvas.Body className="border-bottom px-0 information-main-view py-0">
                <div style={styles.container}>

                    {/* Total amount */}
                    <div style={styles.totalCard}>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>Total amount (₹)</span>
                        <span style={{ fontSize: 20, fontWeight: 500, color: "#111827" }}>
                            {formatINR(totalAmount)}
                        </span>
                    </div>

                    {/* Rows */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                        {rows.map((row, i) => (
                            <div key={row.id} style={styles.card(errorRowIds.has(row.id))}>
                                {/* Row header */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                    <div
                                        style={{
                                            width: 22, height: 22, borderRadius: "50%",
                                            background: "#f3f4f6", fontSize: 11, fontWeight: 500,
                                            color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                        id={`row_${row.id}_${i}`}
                                    >
                                        {i + 1}
                                    </div>
                                    <span id={`installment-name-${row.id}`} style={{ fontSize: 13, fontWeight: 500, color: "#111827", flex: 1 }}>
                                        {row.name || `Installment ${i + 1}`}
                                    </span>
                                    <button
                                        onClick={() => removeRow(row.id)}
                                        style={{
                                            background: "none", border: "none", cursor: "pointer",
                                            fontSize: 14, color: "#9ca3af", padding: "2px 6px", borderRadius: 4,
                                        }}
                                        aria-label="Remove row"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Fields */}
                                <div style={{ ...styles.grid3, gridTemplateColumns: "1.2fr 1fr 1fr 1fr" }}>
                                    {/* Name */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <label style={{ fontSize: 11, color: "#6b7280" }}>Installment name</label>
                                        <input
                                            type="text"
                                            value={row.name}
                                            onChange={(e) => handleNameChange(row.id, e.target.value)}
                                            style={inputBase}
                                            placeholder="e.g. Advance"
                                        />
                                    </div>

                                    {/* Date */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <label style={{ fontSize: 11, color: "#6b7280" }}>Date</label>
                                        <input
                                            type="date"
                                            value={row.payment_date}
                                            onChange={(e) => handleDateChange(row.id, e.target.value)}
                                            style={inputBase}
                                        />
                                    </div>

                                    {/* Percentage */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <label style={{ fontSize: 11, color: "#6b7280" }}>Percentage (%)</label>
                                        <input
                                            type="number"
                                            value={row.percentage}
                                            onChange={(e) => handlePercentageChange(row.id, e.target.value)}
                                            style={{
                                                ...inputBase,
                                                borderColor: row.lastEdited === "percentage" ? "#6b7280" : "#d1d5db",
                                            }}
                                            min={0}
                                            max={100}
                                            step={0.01}
                                            placeholder="e.g. 25"
                                        />
                                    </div>

                                    {/* Amount */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        <label style={{ fontSize: 11, color: "#6b7280" }}>Amount (₹)</label>
                                        <input
                                            type="number"
                                            value={row.amount}
                                            onChange={(e) => handleAmountChange(row.id, e.target.value)}
                                            style={{
                                                ...inputBase,
                                                borderColor: row.lastEdited === "amount" ? "#6b7280" : "#d1d5db",
                                            }}
                                            min={0}
                                            step={1}
                                            placeholder="e.g. 25000"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add row button */}
                    <button style={styles.addBtn} onClick={addRow}>
                        + Add installment
                    </button>

                    {/* Summary bar */}
                    <div style={styles.summaryGrid}>
                        <div style={styles.sumCard(pctOk ? "ok" : sumPct > 100 ? "warn" : "default")}>
                            <div style={{ fontSize: 15, fontWeight: 500, color: pctOk ? "#15803d" : sumPct > 100 ? "#b91c1c" : "#111827" }}>
                                {sumPct.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Total allocated</div>
                        </div>
                        <div style={styles.sumCard("default")}>
                            <div style={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>{formatINR(sumAmt)}</div>
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Total amount</div>
                        </div>
                        <div style={styles.sumCard(remaining < -0.5 ? "warn" : "default")}>
                            <div style={{ fontSize: 15, fontWeight: 500, color: remaining < -0.5 ? "#b91c1c" : "#111827" }}>
                                {formatINR(remaining)}
                            </div>
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Remaining</div>
                        </div>
                    </div>

                    {/* Error banner */}
                    {errors.length > 0 && (
                        <div style={styles.errorBanner}>
                            {errors.map((e, i) => (
                                <div key={i}>• {e.message}</div>
                            ))}
                        </div>
                    )}

                    {/* Success banner */}
                    {submitted && errors.length === 0 && (
                        <div style={styles.successBanner}>
                            Schedule successfully submitted! All installments add up to 100%.
                        </div>
                    )}

                    {/* Submit */}
                    <button style={styles.submitBtn} onClick={handleSubmit}>
                        Submit schedule
                    </button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default PaymentSchedule;